import { useCallback, useEffect, useRef, useState } from 'react'
import { AppState, Platform, View, Text, TouchableOpacity, StyleSheet } from 'react-native'
import * as Updates from 'expo-updates'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { fonts } from '../lib/theme'
import { useThemeColors } from '../lib/useThemeColors'

/**
 * UpdatePrompt — aggiornamenti OTA senza dover chiudere e riaprire l'app due volte.
 *
 * Di default expo-updates controlla all'avvio, scarica in background e applica alla
 * riapertura successiva. Qui in più:
 *  - controlla all'avvio e ogni volta che l'app torna in primo piano (al massimo una volta al minuto);
 *  - se trova un aggiornamento lo scarica subito;
 *  - quando è pronto mostra un avviso con "Riavvia" (non riavvia da solo: si perderebbe
 *    il lavoro in corso, per esempio una formazione o il draft).
 * "Più tardi" nasconde l'avviso fino al prossimo ritorno in primo piano.
 * Non fa nulla su web, in sviluppo o dove gli aggiornamenti sono disattivati.
 */
const MIN_INTERVAL_MS = 60 * 1000   // il controllo è leggero (una richiesta di manifest): 1 minuto basta a non ripeterlo a raffica
const FIRST_CHECK_DELAY_MS = 4000   // non competere con il caricamento iniziale

export default function UpdatePrompt() {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const insets = useSafeAreaInsets()
  const { isUpdatePending } = Updates.useUpdates()
  const [dismissed, setDismissed] = useState(false)
  const lastCheck = useRef(0)

  const enabled = Platform.OS !== 'web' && !__DEV__ && Updates.isEnabled

  const check = useCallback(async () => {
    if (!enabled) return
    if (Date.now() - lastCheck.current < MIN_INTERVAL_MS) return
    lastCheck.current = Date.now()
    try {
      const res = await Updates.checkForUpdateAsync()
      if (res.isAvailable) await Updates.fetchUpdateAsync() // poi useUpdates() segnala isUpdatePending
    } catch { /* rete assente o server non raggiungibile: riproveremo al prossimo ritorno */ }
  }, [enabled])

  useEffect(() => {
    if (!enabled) return
    const timer = setTimeout(check, FIRST_CHECK_DELAY_MS)
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') { setDismissed(false); check() }
    })
    return () => { clearTimeout(timer); sub.remove() }
  }, [enabled, check])

  if (!enabled || !isUpdatePending || dismissed) return null

  return (
    <View pointerEvents="box-none" style={[styles.wrap, { top: insets.top + 8 }]}>
      <View style={[styles.card, { backgroundColor: tc.bgCard2, borderColor: tc.rosa }]} accessibilityRole="alert">
        <View style={styles.texts}>
          <Text style={[styles.title, { color: tc.text }]}>{t('app_update.title')}</Text>
          <Text style={[styles.body, { color: tc.textMuted }]}>{t('app_update.body')}</Text>
        </View>
        <TouchableOpacity
          onPress={() => setDismissed(true)}
          style={styles.later}
          accessibilityRole="button"
        >
          <Text style={[styles.laterText, { color: tc.textMuted }]}>{t('app_update.later')}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => Updates.reloadAsync().catch(() => {})}
          style={[styles.restart, { backgroundColor: tc.rosa }]}
          accessibilityRole="button"
        >
          <Text style={[styles.restartText, { color: tc.brandText }]}>{t('app_update.restart')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  wrap:        { position: 'absolute', left: 12, right: 12, zIndex: 1000, elevation: 10 },
  card:        { flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderRadius: 16, paddingVertical: 10, paddingLeft: 14, paddingRight: 8 },
  texts:       { flex: 1, minWidth: 0 },
  title:       { fontFamily: fonts.condensedBold, fontSize: 15 },
  body:        { fontSize: 12, marginTop: 1 },
  later:       { minHeight: 44, paddingHorizontal: 8, alignItems: 'center', justifyContent: 'center' },
  laterText:   { fontSize: 13, fontWeight: '600' },
  restart:     { minHeight: 44, paddingHorizontal: 16, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  restartText: { fontFamily: fonts.condensedBold, fontSize: 14 },
})
